// Compute the `ReadonlySet<EnrichedFormat>` of currently-active
// formats for the live selection. Used by the host to drive the
// toolbar's active-chip highlights and by the shortcut handler to
// know whether Cmd+B should bold or un-bold.

import type { EnrichedFormat } from '../types';
import { getBlockContaining, getListItemContaining } from './blocks';
import { isInlineMarkActive } from './inline';

const INLINE_LOOKUP: ReadonlyArray<{
  readonly format: EnrichedFormat;
  readonly tag: string;
}> = [
  { format: 'bold', tag: 'STRONG' },
  { format: 'italic', tag: 'EM' },
  { format: 'strikethrough', tag: 'S' },
  { format: 'inlineCode', tag: 'CODE' },
  { format: 'link', tag: 'A' },
];

const BLOCK_LOOKUP: ReadonlyArray<{
  readonly format: EnrichedFormat;
  readonly tag: string;
}> = [
  { format: 'quote', tag: 'BLOCKQUOTE' },
  { format: 'bulletList', tag: 'UL' },
  { format: 'numberedList', tag: 'OL' },
  { format: 'codeBlock', tag: 'PRE' },
];

export function deriveActiveFormats(
  root: HTMLElement,
  selection: Selection | null,
): ReadonlySet<EnrichedFormat> {
  const out = new Set<EnrichedFormat>();
  if (selection === null || selection.rangeCount === 0) return out;
  for (const { format, tag } of INLINE_LOOKUP) {
    if (isInlineMarkActive(root, selection, tag)) out.add(format);
  }
  const block = getBlockContaining(root, selection.anchorNode);
  if (block !== null) {
    // For lists the top-level "block" might be the outer <ul>/<ol>,
    // but the caret could be in a nested list whose kind differs
    // from the outer. Resolve list-kind via the nearest enclosing
    // <li>'s parent. Quote / code-block flags fall through to the
    // top-level lookup.
    const li = getListItemContaining(root, selection.anchorNode);
    if (li !== null) {
      const parent = li.parentElement;
      if (parent !== null) {
        if (parent.tagName === 'UL') out.add('bulletList');
        if (parent.tagName === 'OL') out.add('numberedList');
      }
    }
    for (const { format, tag } of BLOCK_LOOKUP) {
      if (format === 'bulletList' || format === 'numberedList') continue;
      if (block.tagName === tag) out.add(format);
    }
  }
  return out;
}
